import i18n from '@/i18n'

const EMPTY = '—'

type AppLocale = 'zh' | 'en' | 'zh-CN' | 'en-US' | string

function localeFromLanguage(language?: string): 'zh-CN' | 'en-US' {
  const source = language || i18n.language || 'en'
  return source.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'
}

function resolveLocale(locale?: AppLocale): 'zh-CN' | 'en-US' {
  return localeFromLanguage(locale)
}

function finiteNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function parseDateInput(value: number | string | null | undefined): Date | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    const timestampMs = value < 10_000_000_000 ? value * 1000 : value
    const date = new Date(timestampMs)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }

  return null
}

function dateParts(date: Date, locale?: AppLocale) {
  const parts = new Intl.DateTimeFormat(resolveLocale(locale), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
  }
}

function accounting(value: number, formatted: string) {
  if (value >= 0) return formatted
  if (formatted.includes('(') && formatted.includes(')')) return formatted
  return `(${formatted.replace('-', '').trim()})`
}

export function formatNumber(value: number | null | undefined, locale?: AppLocale): string {
  const number = finiteNumber(value)
  if (number === null) return EMPTY
  return new Intl.NumberFormat(resolveLocale(locale), {
    maximumFractionDigits: 2,
  }).format(number)
}

export function formatCount(value: number | null | undefined, locale?: AppLocale): string {
  const number = finiteNumber(value)
  if (number === null) return EMPTY
  return new Intl.NumberFormat(resolveLocale(locale), {
    maximumFractionDigits: 0,
  }).format(Math.round(number))
}

export function formatCredits(value: number | null | undefined, locale?: AppLocale): string {
  const number = finiteNumber(value)
  if (number === null) return EMPTY
  const formatted = new Intl.NumberFormat(resolveLocale(locale), {
    maximumFractionDigits: 0,
  }).format(Math.abs(Math.round(number)))
  return accounting(number, formatted)
}

export function formatCurrency(value: number | null | undefined, currency = 'USD', locale?: AppLocale): string {
  const number = finiteNumber(value)
  if (number === null) return EMPTY
  const formatted = new Intl.NumberFormat(resolveLocale(locale), {
    style: 'currency',
    currency,
    currencySign: 'accounting',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number)
  return accounting(number, formatted)
}

export function formatPercent(value: number | null | undefined, fractionDigits = 0, locale?: AppLocale): string {
  const number = finiteNumber(value)
  if (number === null) return EMPTY
  return new Intl.NumberFormat(resolveLocale(locale), {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(number)
}

// 商业/会计标准: 固定 ISO 8601 YYYY-MM-DD 无歧义 (Eric P0 禁 MM/DD/YYYY); 数字千分位才 locale-aware
export function formatDate(value: number | string | null | undefined, locale?: AppLocale): string {
  const date = parseDateInput(value)
  if (!date) return EMPTY
  const { year, month, day } = dateParts(date, locale)
  return year && month && day ? `${year}-${month}-${day}` : EMPTY
}

// 商业/会计标准: 固定 ISO 8601 YYYY-MM-DD 无歧义 (Eric P0 禁 MM/DD/YYYY); 数字千分位才 locale-aware
export function formatDateTime(value: number | string | null | undefined, locale?: AppLocale): string {
  const date = parseDateInput(value)
  if (!date) return EMPTY
  const { year, month, day, hour, minute } = dateParts(date, locale)
  return year && month && day && hour && minute ? `${year}-${month}-${day} ${hour}:${minute}` : EMPTY
}

export function formatDuration(ms: number | null | undefined, locale?: AppLocale): string {
  const value = finiteNumber(ms)
  if (value === null || value < 0) return EMPTY

  const totalSeconds = value / 1000
  if (totalSeconds < 60) {
    return `${new Intl.NumberFormat(resolveLocale(locale), {
      minimumFractionDigits: totalSeconds < 10 ? 1 : 0,
      maximumFractionDigits: 1,
    }).format(totalSeconds)}s`
  }

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.round(totalSeconds % 60)
  return `${formatCount(minutes, locale)}m ${formatCount(seconds, locale)}s`
}
